import React, { useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import NavigateNext from "@mui/icons-material/NavigateNext";

import useCampuses from "../hooks/useCampuses.js";
import { mondayOfWeek } from "../utils/timetableUtils.js";
import StaffSchedulingPage from "./StaffSchedulingPage";

// Standalone home for the weekly staff scheduling table.
//
// Week navigation is kept LOCAL: it seeds from the global date when the view
// opens (so you land on the same week everything else is on) but stepping
// between weeks here does NOT change the global date, to avoid surprising the
// other boards. Campus selection, by contrast, stays on the shared global
// state so it carries across boards.
export default function StaffSchedulingView({
  selectedDate,
  selectedCampusId,
  setSelectedCampusId,
  onSelectAdult,
  onBack,
  backLabel,
}) {
  const { campuses = [], loading: campusesLoading } = useCampuses();

  // Local week anchor, seeded from the global date at open time.
  const [weekDate, setWeekDate] = useState(selectedDate);

  const selectedCampus = useMemo(
    () => campuses.find((c) => String(c.id) === String(selectedCampusId)) || null,
    [campuses, selectedCampusId]
  );
  const campusName = selectedCampus?.name || "";

  const weekLabel = useMemo(() => {
    const monday = mondayOfWeek(weekDate);
    return monday ? monday.format("D MMM YYYY") : "";
  }, [weekDate]);

  const handlePrevWeek = () => {
    setWeekDate((prev) => prev.subtract(1, "week"));
  };

  const handleNextWeek = () => {
    setWeekDate((prev) => prev.add(1, "week"));
  };

  return (
    <Box sx={{ p: 2 }}>
      {onBack && (
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mb: 1 }}
        >
          {backLabel || "Back"}
        </Button>
      )}

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={handlePrevWeek} aria-label="Previous week">
            <NavigateBefore />
          </IconButton>

          <Box
            sx={{
              px: 2,
              py: 1,
              minWidth: 200,
              textAlign: "center",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "action.hover",
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Week of {weekLabel}
            </Typography>
          </Box>

          <IconButton onClick={handleNextWeek} aria-label="Next week">
            <NavigateNext />
          </IconButton>
        </Stack>

        <FormControl size="small" sx={{ minWidth: 220 }} disabled={campusesLoading}>
          <InputLabel id="staff-campus-label">Campus</InputLabel>
          <Select
            labelId="staff-campus-label"
            value={
              campuses.some((c) => String(c.id) === String(selectedCampusId))
                ? selectedCampusId
                : ""
            }
            label="Campus"
            onChange={(e) => setSelectedCampusId(e.target.value)}
          >
            <MenuItem value="">
              <em>Select a campus</em>
            </MenuItem>

            {campuses.map((campus) => (
              <MenuItem key={campus.id} value={String(campus.id)}>
                {campus.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <StaffSchedulingPage
        selectedDate={weekDate}
        selectedCampusName={campusName}
        onSelectAdult={onSelectAdult}
        campuses={campuses}
      />
    </Box>
  );
}
